import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter, map } from 'rxjs';
import { DAILY_ITEMS, SET_UP_ITEMS } from '../../shell/nav-items';

/** Tablet navigation (700-1023 px): Today, Calendar, Bookings and "More", which holds the Set up pages. */
@Component({
  selector: 'app-bottom-tab-bar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, FaIconComponent, TranslocoDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav
      *transloco="let t"
      [attr.aria-label]="t('nav.main')"
      class="fixed inset-x-0 bottom-0 z-30 bg-surface pb-safe"
    >
      <ul class="grid h-16 grid-cols-4 border-t border-border-default">
        @for (item of tabs; track item.id) {
          <li class="flex">
            <a
              [routerLink]="item.route"
              routerLinkActive="tab-bar-link--active"
              ariaCurrentWhenActive="page"
              class="tab-bar-link"
            >
              <fa-icon [icon]="item.icon" class="size-5 leading-none" aria-hidden="true" />
              <span>{{ t(item.labelKey) }}</span>
            </a>
          </li>
        }
        <li class="flex">
          <a
            routerLink="/more"
            class="tab-bar-link"
            [class.tab-bar-link--active]="moreActive()"
            [attr.aria-current]="moreActive() ? 'page' : null"
          >
            <fa-icon [icon]="moreIcon" class="size-5 leading-none" aria-hidden="true" />
            <span>{{ t('nav.more') }}</span>
          </a>
        </li>
      </ul>
    </nav>
  `,
})
export class BottomTabBarComponent {
  private readonly _router = inject(Router);

  public readonly tabs = DAILY_ITEMS;
  public readonly moreIcon = faEllipsis;

  private readonly _url = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this._router.url },
  );

  // "More" is active on its own page and on every page it links to (Settings is not one on a tablet).
  public readonly moreActive = computed(() => {
    const path = this._url().split(/[?#]/)[0] ?? '';
    return ['/more', ...SET_UP_ITEMS.filter((item) => item.id !== 'settings').map((item) => item.route)].some(
      (route) => path === route || path.startsWith(route + '/'),
    );
  });
}
